// ─────────────────────────────────────────────
//  enterTestMode — wire the live reader and the local writer to captureDay,
//  record the pinned config, and reload into it.
//
//  captureDay is pure orchestration with injected IO; THIS is the file that
//  knows about Firestore, so the two databases meet in exactly one place.
//
//  ⚠️ READS AND WRITES USE DIFFERENT PRIMITIVES ON PURPOSE.
//  Reads go through `firebase/sdk`, which re-exports whichever impl this boot
//  selected — the REAL one, because entering is only ever done from a normal
//  boot. Writes go through `sandbox/localDb`'s own primitives, because its
//  `assertDb` rejects anything that is not a local store: "real and local data
//  must never mix." Using one set for both would either read an empty local
//  store or throw.
//
//  ⚠️ THE RELOAD IS THE LAST STEP AND IT IS NOT OPTIONAL. `sandbox/mode.js`:
//  "`db` is referenced 90 times inside firebase/firestore.js and rebinding it
//  after listeners have attached leaves stale onSnapshot subscriptions that
//  fail SILENTLY."
// ─────────────────────────────────────────────
import { collection, query, where, getDocs } from "../../firebase/sdk";
import { db } from "../../firebase/config";
import {
  createLocalDb,
  doc as localDoc,
  setDoc as localSetDoc,
} from "../../sandbox/localDb";
import {
  TEST_MODE_PERSIST_KEY,
  writeTestMode,
  clearTestMode,
  isTestModeBoot,
} from "../../domain/testMode";
import { isSnapshotUsable, missingCollections } from "../../domain/testSnapshot";
import { dayRosterFrom, viewAsProfileFor } from "../../domain/testRoster";
import captureDay from "./captureDay";

// Firestore rejects an `in` filter with more than 30 values, so roster reads
// are chunked. A silent truncation here would drop clinicians from the
// snapshot and they would simply not exist in the testing session.
const IN_LIMIT = 30;
const chunk = (arr, n) => {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

const snap = async (q) => {
  const s = await getDocs(q);
  return s.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Read one collection from LIVE Firestore at the scope the plan asks for.
 * Exported for tests and so the pre-entry screen can preview a count before
 * committing to a reload.
 */
export async function readFromLive(name, { facilityId, date, scope, ids, dateField = "date" }) {
  const col = collection(db, name);
  if (scope === "facility") {
    return snap(query(col, where("facilityId", "==", facilityId)));
  }
  if (scope === "day") {
    // ⚠️ `dateField` COMES FROM THE PLAN, because it is not `date` everywhere —
    // tasks use `dateKey`. Filtering on a field a collection lacks returns zero
    // rows with no error, and the manifest then reports a genuinely quiet day.
    return snap(
      query(col, where("facilityId", "==", facilityId), where(dateField, "==", date))
    );
  }
  if (scope === "tenant") {
    return snap(query(col));
  }
  if (scope === "roster") {
    // Empty is handled upstream by captureDay (it captures nobody rather than
    // everybody); this stays defensive because the consequence of getting it
    // wrong is pulling the whole auth collection.
    if (!Array.isArray(ids) || ids.length === 0) return [];
    const batches = await Promise.all(
      chunk(ids, IN_LIMIT).map((slice) =>
        snap(query(col, where("__name__", "in", slice)))
      )
    );
    return batches.flat();
  }
  throw new Error(`[testing] unknown snapshot scope: ${scope}`);
}

/**
 * Who was working at this facility on this day, read from LIVE Firestore so the
 * picker can be filled BEFORE anything is captured or any flag is set.
 *
 * Reading it first is what lets the screen refuse a day nobody can be
 * impersonated on, instead of capturing a snapshot and then presenting an empty
 * picker the tester cannot act on.
 */
export async function readDayRoster(facilityId, date) {
  const [assignments, boards, staff] = await Promise.all([
    readFromLive("assignments", { facilityId, date, scope: "day" }),
    readFromLive("daily_boards", { facilityId, date, scope: "day" }),
    readFromLive("staff", { facilityId, date, scope: "tenant" }),
  ]);
  return dayRosterFrom({ assignments, board: boards[0] || null, staff });
}

/**
 * A writer into a FRESH local store for this snapshot. Returns the store so the
 * caller can await its readiness before reloading — a reload that races the
 * persistence write boots into a half-filled day.
 */
export function makeLocalTarget() {
  const target = createLocalDb({ persistKey: TEST_MODE_PERSIST_KEY });
  const writeDocs = async (name, docs) => {
    for (const d of docs) {
      if (!d?.id) continue;
      const { id, ...data } = d;
      await localSetDoc(localDoc(target, name, id), data);
    }
  };
  return { target, writeDocs };
}

export const hardReload = (path = "/") => {
  window.location.assign(path);
};

/**
 * Capture the day, pin the config, reload.
 *
 * Returns the manifest on refusal (so the caller can SHOW what was missing)
 * and never reloads on a snapshot that cannot render a board — a tester
 * dropped into an empty hospital would reasonably conclude the day was empty.
 */
export async function enterTestMode(
  {
    facilityId, facilityName = "", date,
    viewAsUid, viewAsName = "", viewAsRole = "provider", rosterUids = [],
    // ⚠️ THE PERSON ENTERING MUST BE IN THE SNAPSHOT, AND THEY ARE OFTEN NOT ON
    // THE ROSTER. An admin is usually not working a clinical room that day, so
    // their `users` doc would be missing from a roster-scoped capture — and
    // AuthContext does NOT fail loudly when a profile is absent. It takes the
    // bootstrap branch (AuthContext.js:121-135), calls createUser() with
    // buildDefaultProfile(), and SILENTLY PROVISIONS A LEAST-PRIVILEGE ACCOUNT
    // into the local store. The admin would quietly lose admin rights inside
    // the session, with no error and nothing on screen to explain it.
    enteringUid = null,
  },
  { reload = hardReload, landing = "/board" } = {}
) {
  // De-duplicated: the entering user may also be on the roster, and asking
  // Firestore for the same id twice in one `in` filter wastes a slot against
  // the 30-value limit.
  const captureUids = [...new Set([...(rosterUids || []), enteringUid].filter(Boolean))];
  // Hold the store, not just its writer: the reload must not race persistence.
  const { target, writeDocs } = makeLocalTarget();

  const manifest = await captureDay({
    facilityId,
    date,
    rosterUids: captureUids,
    readCollection: readFromLive,
    writeDocs,
    alreadyInTestMode: isTestModeBoot(),
  });

  if (!isSnapshotUsable(manifest)) {
    // Leave nothing behind: a flag without a usable store is how a boot lands
    // in a testing session it cannot render.
    clearTestMode();
    return { ok: false, manifest };
  }

  const cfg = writeTestMode({
    facilityId, facilityName, date, viewAsUid, viewAsName,
    missing: missingCollections(manifest),
  });
  if (!cfg) return { ok: false, manifest };

  // ⚠️ ACTUALLY WEAR THE IDENTITY. Storing viewAsUid in the config blob only
  // told the BANNER who to name; AuthContext never read it, so the app rendered
  // as the admin while the banner announced "as Ana Ruiz". A banner asserting
  // an identity the app is not wearing is worse than no banner.
  //
  // ⚠️ sessionStorage, NOT localStorage. AuthContext persists viewAs in
  // sessionStorage (AuthContext.js:43, :298) — writing the other one is a
  // silent no-op that looks correct in a diff.
  //
  // Writing the key directly bypasses setViewAs's own admin check, but the gate
  // still holds where it matters: AuthContext only RESOLVES a stored viewAs for
  // a real admin, so a forged key grants a non-admin nothing.
  const viewAsTarget = viewAsProfileFor({ uid: viewAsUid, name: viewAsName, role: viewAsRole });
  if (viewAsTarget) {
    try {
      sessionStorage.setItem("atc.viewAs", JSON.stringify(viewAsTarget));
    } catch {
      /* hostile storage: the session still works, just as the admin */
    }
  }

  // ⚠️ WAIT FOR THE STORE BEFORE RELOADING. createLocalDb exposes `ready`;
  // reloading while persistence is still settling boots into a half-written
  // day, which looks exactly like a facility that had a quiet morning.
  try {
    await target?.flushNow?.();
  } catch {
    /* a store that cannot persist still serves from memory for this session */
  }

  reload(landing);
  return { ok: true, manifest, config: cfg };
}

export default enterTestMode;
